import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AlertsGridButtons } from '../alerts-grid-buttons';
import '../../prop-types/alert.types';
import { AlertRow } from '../alert-row';
import { AlertsGridHeader } from '../alerts-grid-header';
import { AlertsLoadMore } from '../alerts-load-more/';
import { getMoreOrderAlerts } from '../../../../../store/alerts/alerts.actions';
import { alertDismissalRequest } from '../../../../../store/alerts/grid/alerts.grid.actions';
import style from '../style.module.less';

/**
 * @param {object} props
 * @param {AlertPropType[]} props.alerts
 * @param {() => void} props.closeModal
 * @returns {JSX.Element}
 */
export const AlertsGrid = ({ alerts, closeModal }) => {
  const dispatch = useDispatch();
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage, setNextPage] = useState(2);
  const [localAlerts, setAlerts] = useState(alerts);
  const [selectedAlertIds, setSelectedAlertIds] = useState([]);

  const onFailureHandler = useCallback(() => {
    setErrorOccurred(true);
    setIsLoading(false);
  }, [setErrorOccurred, setIsLoading]);

  const onSuccessHandler = useCallback(
    (newAlerts) => {
      setAlerts([...localAlerts, ...newAlerts]);
      setNextPage(nextPage + 1);
      setIsLoading(false);
    },
    [localAlerts, nextPage, setAlerts, setNextPage, setIsLoading],
  );

  const loadMore = useCallback(() => {
    setIsLoading(true);
    dispatch(getMoreOrderAlerts(nextPage, onSuccessHandler, onFailureHandler));
  }, [nextPage, dispatch, setIsLoading, onSuccessHandler, onFailureHandler]);

  const onDismissSuccess = useCallback(() => {
    setAlerts(localAlerts.filter((alert) => !selectedAlertIds.includes(alert.alertID)));
    setSelectedAlertIds([]);
  }, [localAlerts, selectedAlertIds, setAlerts]);

  const handleAlertDismissal = useCallback(() => {
    dispatch(alertDismissalRequest(selectedAlertIds, onDismissSuccess));
  }, [selectedAlertIds, onDismissSuccess, dispatch]);

  const handleCheckboxChange = useCallback(
    (alertID) => {
      const alertIds = selectedAlertIds.includes(alertID)
        ? selectedAlertIds.filter((id) => id !== alertID)
        : [...selectedAlertIds, alertID];
      setSelectedAlertIds(alertIds);
    },
    [selectedAlertIds, setSelectedAlertIds],
  );

  const handleSelectAll = useCallback(() => {
    const alertsToSet = localAlerts.length === selectedAlertIds.length ? [] : localAlerts.map((alert) => alert.alertID);
    setSelectedAlertIds(alertsToSet);
  }, [localAlerts, selectedAlertIds, setSelectedAlertIds]);

  const areAllSelected = localAlerts.length === selectedAlertIds.length;
  return (
    <div>
      <AlertsGridButtons errorOccurred={errorOccurred} onAlertDismissal={handleAlertDismissal} selectedAlertIds={selectedAlertIds} />
      <AlertsGridHeader isSelected={areAllSelected} onSelectAllChanged={handleSelectAll} />
      <div className={style['alert-rows-wrapper']}>
        {localAlerts.map((alert) => {
          const isSelected = selectedAlertIds.includes(alert.alertID);
          return (
            <AlertRow key={alert.alertID} alert={alert} closeModal={closeModal} isSelected={isSelected} onChange={handleCheckboxChange} />
          );
        })}
        <AlertsLoadMore fetchedOrderAlertCount={localAlerts.length} isLoading={isLoading} onLoadMore={loadMore} />
      </div>
    </div>
  );
};
